/**
 * Structured JSON logger for email & scheduler subsystems.
 * Zero dependencies — writes one JSON line per call to stdout (stderr for errors).
 * LOG_LEVEL env var controls verbosity: debug | info | warn | error (default: info).
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

function currentLevel(): LogLevel {
  const env = (process.env.LOG_LEVEL || 'info').toLowerCase();
  return env in LEVEL_ORDER ? (env as LogLevel) : 'info';
}

function shouldLog(level: LogLevel): boolean {
  return LEVEL_ORDER[level] >= LEVEL_ORDER[currentLevel()];
}

function write(level: LogLevel, msg: string, context?: Record<string, unknown>): void {
  if (!shouldLog(level)) return;

  const entry = {
    level,
    msg,
    timestamp: new Date().toISOString(),
    ...context,
  };

  const line = JSON.stringify(entry);
  if (level === 'error') {
    process.stderr.write(line + '\n');
  } else {
    process.stdout.write(line + '\n');
  }
}

export const logger = {
  debug: (msg: string, context?: Record<string, unknown>) => write('debug', msg, context),
  info:  (msg: string, context?: Record<string, unknown>) => write('info', msg, context),
  warn:  (msg: string, context?: Record<string, unknown>) => write('warn', msg, context),
  error: (msg: string, context?: Record<string, unknown>) => write('error', msg, context),
};
